import type { ComponentContentSchema, PageDataMap } from '@vibesites/runtime';
import { isActivePage, mergePages } from '@vibesites/runtime';
import { useCollectionBinding, usePropertyField, useVibesites } from '@vibesites/runtime/react';
import { pages as parentPages } from '@vibesites/theme-preferred/pages';
import localPagesData from '../pages.json';

interface VirtualTour {
  virtual_tour_url?: string | null;
}

interface Review {
  rating?: number | string | null;
  review_text?: string | null;
}

interface Faq {
  question?: string | null;
  answer?: string | null;
}

/**
 * Upstream feeds sometimes pass literal strings like "false", "null", "none"
 * instead of an empty value — treat those as missing.
 */
function isUsableUrl(value: string | null | undefined): boolean {
  if (!value) return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  const lower = trimmed.toLowerCase();
  if (lower === 'false' || lower === 'true' || lower === 'null' || lower === 'undefined' || lower === 'none' || lower === 'n/a') {
    return false;
  }
  return true;
}

function normalizePath(path: string): string {
  const trimmed = path.trim().split(/[?#]/)[0].replace(/\/+$/, '');
  return (trimmed || '/').toLowerCase();
}

function pageLabelFromAlias(alias: string): string {
  return alias
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function isScheduleATourPage(p: { alias?: string; path?: string }): boolean {
  return p.alias === 'schedule-a-tour' || p.path === '/schedule-a-tour';
}

function isFaqPage(p: { alias?: string; path?: string }): boolean {
  return (
    p.alias === 'faq' ||
    p.path === '/faq' ||
    p.path === '/frequently-asked-questions'
  );
}

/**
 * Merge parent-theme pages with this company's pages.json so the sitemap
 * respects child `visible: false` overrides (the inherited SitemapContent
 * imported only the parent's pages.json).
 */
const mergedPages = mergePages(
  Object.fromEntries(
    Object.entries(parentPages).map(([alias, page], index) => [
      alias,
      {
        name: pageLabelFromAlias(alias),
        path: page.path ?? `/${alias}`,
        position: index,
        visible: page.visible ?? true,
        template: page.template,
      },
    ]),
  ) as PageDataMap,
  localPagesData as PageDataMap,
);

// True when the Schedule A Tour page includes the tour scheduler section
// (the widget that loads available tour types/slots at runtime).
const scheduleATourHasScheduler = mergedPages.some(
  (p) => isScheduleATourPage(p) && (p.sections || []).some((s) => String(s.slot) === 'ScheduleATour'),
);

// Active, visible pages (plus Schedule A Tour when its tour scheduler is on
// the page — even if hidden from nav). Excludes the sitemap page itself and
// template pages (e.g., hyperlocal).
const allPages = mergedPages
  .filter((p) => {
    if (!isActivePage(p) || p.path === '/sitemap' || p.template) return false;
    if (isScheduleATourPage(p)) return scheduleATourHasScheduler;
    return p.visible !== false;
  })
  .sort((a, b) => a.position - b.position);

export function SitemapContent() {
  // Hide content-dependent pages when the property has nothing to show
  // (matches VirtualTourEmbed / Reviews / FaqList empty states).
  const virtualTours = useCollectionBinding<VirtualTour>('sitemapcontent.virtual_tours', {
    collection: 'virtual_tours',
  });
  const reviews = useCollectionBinding<Review>('sitemapcontent.reviews', {
    collection: 'reviews',
  });
  const faqs = useCollectionBinding<Faq>('sitemapcontent.faqs', {
    collection: 'faqs',
  });
  const hasVirtualTours = (virtualTours || []).some((t) => isUsableUrl(t.virtual_tour_url));
  const hasReviews = (reviews || []).length > 0;
  const hasFaqs = (faqs || []).length > 0;
  const engrainSightmapFields = [
    usePropertyField('custom_engrain_sightmap_url'),
    usePropertyField('custom_engrain_sitemap_url'),
    usePropertyField('custom_engrain_sightmap_id'),
    usePropertyField('custom_engrain_sitemap_id'),
  ] as Array<string | null | undefined>;
  const hasInteractiveMap = engrainSightmapFields.some(isUsableUrl);

  // `pagePaths` lists the pages this site actually ships, so it reflects pages
  // hidden or deleted in page settings, which the static pages.json merge above
  // can't see. When absent, fall back to the static merge only.
  const { pagePaths } = useVibesites();
  const shippedPaths = pagePaths?.length ? new Set(pagePaths.map(normalizePath)) : null;

  const pages = allPages.filter((p) => {
    if (shippedPaths && !shippedPaths.has(normalizePath(p.path))) return false;
    if (p.path === '/virtual-tours' && !hasVirtualTours) return false;
    if (p.path === '/interactive-map' && !hasInteractiveMap) return false;
    if (p.path === '/reviews' && !hasReviews) return false;
    if (isFaqPage(p) && !hasFaqs) return false;
    return true;
  });

  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          {pages.map((page) => (
            <a
              key={page.path}
              href={page.path}
              className="block border border-black rounded px-6 py-4 text-center font-body text-sm text-black hover:bg-black hover:text-white transition-colors"
            >
              {page.name}
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'SitemapContent',
  displayName: 'Sitemap',
  tokens: {},
  collectionBindings: {
    'sitemapcontent.virtual_tours': {
      label: 'Virtual Tours (visibility check)',
      collection: 'virtual_tours',
    },
    'sitemapcontent.reviews': {
      label: 'Reviews (visibility check)',
      collection: 'reviews',
    },
    'sitemapcontent.faqs': {
      label: 'FAQs (visibility check)',
      collection: 'faqs',
    },
  },
};

(SitemapContent as any).contentSchema = contentSchema;

export default SitemapContent;
